import { Minimize2 } from "lucide-react";
import { CSSProperties, useMemo } from "react";
import { useNavigate } from "react-router";

import { runViewTransition } from "../../../../run-view-transition";
import { useDocumentTitle } from "../../../../use-document-title";
import { ScrollShadow } from "../../../../scroll-shadow";
import { useInfiniteRows } from "../../../../use-infinite-rows";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useSimulatedLoad } from "../../../../use-simulated-load";
import styles from "./slowest-tests.module.css";

/**
 * Full-page `/qa-automation/slowest-tests` route: the "maximised" ranked-list
 * view of the QA automation dashboard's "5 slowest tests" card
 * (`../recent-runs.tsx`'s `SLOWEST_TESTS` / `RankedCard`). Deliberately NOT
 * importing from that file (it's owned by a concurrently-edited route) — the
 * `RankedRow` type and its 5 hand-authored rows are copied verbatim below,
 * then extended with deterministic synthetic rows for infinite scroll. All
 * data is static mock content — there is no live QA-automation backend in
 * `platform` yet.
 */

type RankedRow = {
  primary: string;
  context: string;
  primaryValue: string;
  secondaryValue: string;
  /** 0..1, how far the inline bar fills. */
  barFraction: number;
  barTone: "duration" | "fail";
};

// Verbatim copy of `../recent-runs.tsx`'s `SLOWEST_TESTS` — the hand-authored
// "recent history" sample the dashboard card shows. `barFraction` values
// below are recomputed further down (see `ALL_SLOWEST_TESTS`) against the
// true slowest duration across the FULL extended list, not just these 5.
const SLOWEST_TESTS: RankedRow[] = [
  {
    primary: "Deal pipeline — bulk stage transition for 200 companies",
    context: "desktop › pipeline › bulk actions",
    primaryValue: "42.8s",
    secondaryValue: "18 runs",
    barFraction: 1,
    barTone: "duration",
  },
  {
    primary: "Portfolio CSV export — 5k row workbook",
    context: "desktop › portfolio › export",
    primaryValue: "38.1s",
    secondaryValue: "24 runs",
    barFraction: 38.1 / 42.8,
    barTone: "duration",
  },
  {
    primary: "Fund report — PDF render with embedded charts",
    context: "desktop › reports › fund",
    primaryValue: "31.6s",
    secondaryValue: "12 runs",
    barFraction: 31.6 / 42.8,
    barTone: "duration",
  },
  {
    primary: "Company profile — attach 20 documents",
    context: "desktop › company › documents",
    primaryValue: "27.4s",
    secondaryValue: "30 runs",
    barFraction: 27.4 / 42.8,
    barTone: "duration",
  },
  {
    primary: "Investor onboarding — KYC document upload",
    context: "mobile › onboarding › kyc",
    primaryValue: "24.9s",
    secondaryValue: "15 runs",
    barFraction: 24.9 / 42.8,
    barTone: "duration",
  },
];

/**
 * A slow QA suite realistically has far more than 5 tests worth ranking —
 * `TOTAL_SLOWEST_TEST_COUNT` is the real, accurate total this page claims
 * (shown in the header's count badge), and `getSlowestTest(index)` below
 * extends the hand-authored `SLOWEST_TESTS` array out to that count with
 * deterministic synthetic rows for infinite scroll. Same FNV-1a hash +
 * mulberry32 PRNG technique as `../../workflows/pages/runs.tsx`'s
 * `generateSyntheticRun`, so the SAME index always produces the SAME
 * synthetic row (no `Math.random()`/`Date.now()`).
 */
const TOTAL_SLOWEST_TEST_COUNT = 55;

function hashSeed(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const AREA_POOL = [
  "Deal pipeline",
  "Portfolio dashboard",
  "Fund report",
  "Company profile",
  "Investor onboarding",
  "Admin console",
  "Billing",
  "Notifications",
  "Search",
  "Document viewer",
  "Calendar sync",
  "Settings",
];

const ACTION_POOL = [
  "bulk stage transition",
  "CSV export",
  "PDF render with embedded charts",
  "attach documents",
  "permission sync",
  "webhook retry",
  "currency conversion",
  "chart render",
  "search reindex",
  "file compression",
  "email digest render",
  "access audit",
  "report export",
  "token refresh",
  "virtualized scroll render",
];

const CONTEXT_PREFIX_POOL = ["desktop", "mobile", "web"];
const CONTEXT_MID_POOL = [
  "pipeline",
  "portfolio",
  "reports",
  "company",
  "onboarding",
  "admin",
  "billing",
  "notifications",
  "search",
  "documents",
  "calendar",
  "settings",
];
const CONTEXT_LEAF_POOL = [
  "bulk actions",
  "export",
  "fund",
  "documents",
  "kyc",
  "permissions",
  "webhooks",
  "currency",
  "charts",
  "reindex",
  "compression",
  "digest",
  "audit",
  "filters",
  "refresh",
];

const LAST_HARDCODED_DURATION_SECONDS = 24.9;

/** Fixed per-row decrement plus jitter STRICTLY SMALLER than the step, same
 * technique as `../../workflows/pages/active.tsx`'s
 * `generateSyntheticActiveRun` — this list is duration-sorted slowest-first,
 * so each synthetic row's duration must stay strictly less than the row
 * before it. A fixed step per `rowsBeyond` (not a per-row recursive
 * subtraction) plus jitter smaller than that step guarantees the sequence
 * never reorders, no matter how the jitter rolls. */
const DURATION_STEP_SECONDS = 0.42;
const DURATION_JITTER_RANGE_SECONDS = 0.3; // < DURATION_STEP_SECONDS

/** A test beyond the hand-authored `SLOWEST_TESTS` sample — deterministic
 * per `index`, so scrolling to the same position always shows the same row.
 * `barFraction` is left at 0 here; the real value (relative to the true
 * slowest duration across the whole extended list) is computed once in
 * `ALL_SLOWEST_TESTS` below. */
function generateSyntheticSlowestTest(index: number): RankedRow {
  const rand = mulberry32(hashSeed(`slowest-test-${index}`));

  const area = AREA_POOL[Math.floor(rand() * AREA_POOL.length)];
  const action = ACTION_POOL[Math.floor(rand() * ACTION_POOL.length)];
  const prefix = CONTEXT_PREFIX_POOL[Math.floor(rand() * CONTEXT_PREFIX_POOL.length)];
  const mid = CONTEXT_MID_POOL[Math.floor(rand() * CONTEXT_MID_POOL.length)];
  const leaf = CONTEXT_LEAF_POOL[Math.floor(rand() * CONTEXT_LEAF_POOL.length)];

  const rowsBeyond = index - SLOWEST_TESTS.length + 1;
  const jitterSeconds =
    rand() * DURATION_JITTER_RANGE_SECONDS - DURATION_JITTER_RANGE_SECONDS / 2;
  const durationSeconds = Math.max(
    1.2,
    LAST_HARDCODED_DURATION_SECONDS -
      rowsBeyond * DURATION_STEP_SECONDS +
      jitterSeconds,
  );

  const runCount = 6 + Math.floor(rand() * 35);

  return {
    primary: `${area} — ${action}`,
    context: `${prefix} › ${mid} › ${leaf}`,
    primaryValue: `${durationSeconds.toFixed(1)}s`,
    secondaryValue: `${runCount} runs`,
    barFraction: 0, // recomputed in ALL_SLOWEST_TESTS
    barTone: "duration",
  };
}

/** The full deterministic dataset (hand-authored + synthetic), with every
 * row's `barFraction` recomputed against the TRUE slowest duration across
 * the whole list — matching `../recent-runs.tsx`'s own
 * `barFraction = value / maxValue` pattern, just extended past 5 rows. */
const ALL_SLOWEST_TESTS: RankedRow[] = (() => {
  const rows = Array.from({ length: TOTAL_SLOWEST_TEST_COUNT }, (_, index) =>
    index < SLOWEST_TESTS.length ? SLOWEST_TESTS[index] : generateSyntheticSlowestTest(index),
  );
  const maxDuration = Math.max(...rows.map((row) => parseFloat(row.primaryValue)));
  return rows.map((row) => ({
    ...row,
    barFraction: parseFloat(row.primaryValue) / maxDuration,
  }));
})();

function getSlowestTest(index: number): RankedRow {
  return ALL_SLOWEST_TESTS[index];
}

/** Shimmering placeholder `<li>` shaped like a real `.rankedRow` — same
 * varied-width-per-field trick as `../recent-runs.tsx`'s
 * `RankedRowSkeleton`, copied here since that component isn't exported (and
 * this page's `<ol>` needs its own skeleton/sentinel `<li>`s rather than the
 * `<tr>`-shaped `SkeletonRow`/`InfiniteScrollSentinel` from
 * `../../../../skeleton-row.tsx`, which only fit `<table>` layouts). */
function RankedRowSkeleton({ index }: { index: number }) {
  return (
    <li className={styles.rankedRow} aria-hidden="true">
      <span className={styles.rankedIndex}>{index + 1}</span>
      <div className={styles.rankedMain}>
        <div
          className={styles.skeletonBar}
          style={{ height: 13, width: `${62 + ((index * 13) % 30)}%` }}
        />
        <div
          className={styles.skeletonBar}
          style={{ height: 11, width: `${38 + ((index * 11) % 24)}%`, marginTop: 5 }}
        />
        <div
          className={styles.skeletonBar}
          style={{ height: 6, width: "100%", marginTop: 6, borderRadius: "var(--radius-full)" }}
        />
      </div>
      <div className={styles.rankedValues}>
        <div className={styles.skeletonBar} style={{ height: 13, width: 40 }} />
        <div
          className={styles.skeletonBar}
          style={{ height: 11, width: 52, marginTop: 5, marginLeft: "auto" }}
        />
      </div>
    </li>
  );
}

export function QaSlowestTestsPage() {
  useDocumentTitle("5 slowest tests");

  const navigate = useNavigate();
  const goToDashboard = () => runViewTransition(() => navigate("/qa-automation"));

  // Initial page-mount loading state — distinct from `isLoadingMore` below,
  // which only covers the infinite-scroll "load more" case further down the
  // list.
  const isInitialLoading = useSimulatedLoad();

  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const { loadedCount, isLoadingMore, hasMore, sentinelRef } = useInfiniteRows({
    totalCount: TOTAL_SLOWEST_TEST_COUNT,
    initialCount: SLOWEST_TESTS.length,
    pageSize: 15,
    scrollRootRef: tableWrapRef,
  });

  const loadedRows = useMemo(
    () => Array.from({ length: loadedCount }, (_, index) => getSlowestTest(index)),
    [loadedCount],
  );

  return (
    <div className={styles.page}>
      <section
        className={styles.card}
        style={{ viewTransitionName: "qa-card-slowest-tests" } as unknown as CSSProperties}
      >
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>5 slowest tests</h2>
            <span className={styles.countBadge}>{TOTAL_SLOWEST_TEST_COUNT}</span>
          </div>
          <button
            type="button"
            className={styles.minimiseButton}
            onClick={goToDashboard}
            aria-label="Minimise to dashboard"
            title="Minimise to dashboard"
          >
            <Minimize2 className={styles.minimiseIcon} aria-hidden="true" />
          </button>
        </header>

        <div className={styles.scrollShadowWrapper}>
          <div ref={tableWrapRef} className={styles.tableWrap}>
            <ol className={styles.rankedList}>
              {isInitialLoading ? (
                Array.from({ length: 8 }, (_, index) => (
                  <RankedRowSkeleton key={`initial-skeleton-${index}`} index={index} />
                ))
              ) : (
                <>
                  {loadedRows.map((row, idx) => (
                    <li key={row.primary} className={styles.rankedRow} title={row.primary}>
                      <span className={styles.rankedIndex}>{idx + 1}</span>
                      <div className={styles.rankedMain}>
                        <div className={styles.rankedPrimary}>{row.primary}</div>
                        {row.context && (
                          <div className={styles.rankedContext}>{row.context}</div>
                        )}
                        <div className={styles.rankedBarTrack}>
                          <div
                            className={`${styles.rankedBarFill} ${styles.rankedBarDuration}`}
                            style={{
                              width: `${Math.round(Math.max(0, Math.min(1, row.barFraction)) * 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                      <div className={styles.rankedValues}>
                        <div className={styles.rankedPrimaryValue}>{row.primaryValue}</div>
                        <div className={styles.rankedSecondaryValue}>{row.secondaryValue}</div>
                      </div>
                    </li>
                  ))}
                  {hasMore && (
                    <li aria-hidden="true">
                      <div ref={sentinelRef} className={styles.sentinel} />
                    </li>
                  )}
                  {isLoadingMore &&
                    Array.from({ length: 3 }, (_, index) => (
                      <RankedRowSkeleton key={`skeleton-${index}`} index={index} />
                    ))}
                </>
              )}
            </ol>
          </div>
          <ScrollShadow atTop={atTop} atBottom={atBottom} />
        </div>
      </section>
    </div>
  );
}
